// The fields dataset: one descriptor row per FILTERABLE field per dataset,
// generated from the same registries that build the search schemas and drive
// API enum enforcement. This is the on-demand vocabulary behind the
// search_fields / get_fields tools; the tool schemas advertise structure and
// point here for values. Write-only fields are not rows.
import {
  type FieldDescriptor,
  type FieldEnumValue,
  type RelationalDescriptor,
  SEARCH_SCHEMAS,
  type SearchSchemaDescriptor,
} from './data-api.js';

export type FieldsDatasetKind = 'enum' | 'text' | 'number' | 'date' | 'relation';

export interface FieldsDatasetSubField {
  key: string;
  kind: Exclude<FieldsDatasetKind, 'relation'>;
  description: string;
  values?: readonly FieldEnumValue[];
}

export interface FieldsDatasetDescriptor {
  dataset: string;
  key: string;
  kind: FieldsDatasetKind;
  description: string;
  values?: readonly FieldEnumValue[];
  // Present iff kind === 'relation'; exactly one level deep.
  sub_fields?: readonly FieldsDatasetSubField[];
}

export const FIELDS_DATASET_IDS = [
  'accounts',
  'projects',
  'contacts',
  'signals',
  'workspace_accounts',
  'workspace_projects',
  'workspace_contacts',
] as const;

// Tripwire for the unthrottled return-everything route: static registry
// metadata measured 110,859 bytes on 2026-07-21 after description rewrites;
// this bound trips on runaway registry growth, not normal additions.
export const FIELDS_DATASET_MAX_BYTES = 131072;

function scalarRow(field: FieldDescriptor): FieldsDatasetSubField {
  return field.kind === 'enum'
    ? { key: field.key, kind: 'enum', description: field.description, values: field.values }
    : { key: field.key, kind: field.kind, description: field.description };
}

function relationRow(
  dataset: string,
  key: string,
  rel: RelationalDescriptor,
): FieldsDatasetDescriptor {
  return {
    dataset,
    key,
    kind: 'relation',
    description: rel.description,
    sub_fields: [...rel.filterFields, ...rel.rangeFields].map(scalarRow),
  };
}

function datasetRows(dataset: string, schema: SearchSchemaDescriptor): FieldsDatasetDescriptor[] {
  return [
    ...schema.filterFields.map((f) => ({ dataset, ...scalarRow(f) })),
    ...schema.rangeFields.map((f) => ({ dataset, ...scalarRow(f) })),
    ...Object.entries(schema.relational ?? {}).map(([key, rel]) => relationRow(dataset, key, rel)),
  ];
}

/** The global datasets; the API adds the workspace datasets from the @parcel/filters field list. */
export function buildFieldsDataset(): FieldsDatasetDescriptor[] {
  return Object.entries(SEARCH_SCHEMAS).flatMap(([dataset, schema]) =>
    datasetRows(dataset, schema),
  );
}
